/* eslint-disable @typescript-eslint/no-explicit-any */

export type CachedFunctionOptions<T extends (...args: any[]) => any> = {
  id: string;
  inputFunction: T;
  expirationSelector?: (result: Awaited<ReturnType<T>>) => number;
  warmUpOnServiceStart?: boolean;
};

const warmUpFunctions: Array<(...args: any[]) => any> = [];

export const getCachedFunction = <T extends (...args: any[]) => any>({
  id,
  inputFunction,
  expirationSelector,
  warmUpOnServiceStart,
}: CachedFunctionOptions<T>): T => {
  let expiration: number;
  let cachedPromise: Promise<ReturnType<T>> | undefined;
  let fulfilled = false;
  const cachedFunction = (async (...args: Parameters<T>) => {
    try {
      // use the cached promise whenever is fulfilled and not expired, or when is not
      // fulfilled since this means is in progress and we are synchronizing access.
      if (
        (cachedPromise && fulfilled && expiration > Date.now()) ||
        (cachedPromise && !fulfilled)
      ) {
        return cachedPromise;
      }
      fulfilled = false;
      cachedPromise = inputFunction(...args) as Promise<ReturnType<T>>;
      const data = await cachedPromise;
      fulfilled = true;
      expiration = expirationSelector ? expirationSelector(data) : Number.MAX_VALUE;
      console.log(
        `[${id}][Cache] Data acquired and cached for ${
          expiration === Number.MAX_VALUE
            ? "Number.MAX_VALUE"
            : Math.max((expiration - Date.now()) / 60000, 0)
        } minutes`
      );
      return cachedPromise;
    } catch (e) {
      // We need to clear the cache state for retries
      cachedPromise = undefined;
      fulfilled = false;
      expiration = 0;
      console.error(`[${id}][Cache] Error when getting data: ${e}`);
      throw e;
    }
  }) as T;
  if (warmUpOnServiceStart) {
    warmUpFunctions.push(cachedFunction);
  }
  return cachedFunction;
};

export const warmUpCache = async () => {
  try {
    console.log("Warming up the cache");
    await Promise.all(warmUpFunctions.map(cachedFunction => cachedFunction()));
  } catch (e) {
    console.log("Failed to warm up the cache");
  }
};
